// Cooperative write backpressure (WIRE-CONTRACT §write): a write response may carry
// `write_backoff_ms`, "the client SHOULD pace subsequent writes by at least this many ms".
// The gateway's own curve caps the hint at 2000ms, so honouring it costs a paced client
// nothing in practice; ignoring it costs the gateway a hot loop it asked us to slow down.
//
// Each engine holds ONE timestamp of state, the earliest wall clock at which its next gateway
// write may leave. No timer is held: the wait happens on the send path, so a hint from a write
// that nobody follows up on expires unobserved. status.syncing stays true across a paced gap,
// deliberately, because the call has not resolved and will still put a request on the wire.
//
// Defensive ceiling on the hint. A gateway answering 10 minutes would otherwise wedge an
// aggregate's writes with no way for the app to tell the difference from a hang. 30s is far
// above the contract's 2000ms cap, so a hint above it is a broken gateway, not backpressure.
//
// The bound applies at BOTH ends. `now` is a wall clock (NTP, suspend/resume, a user setting
// the time), so a backward step between stamp and wait reopens `notBefore - now()` to any size.
// The second clamp caps that step; the wait runs longer than the gateway asked for, but bounded.
export const MAX_WRITE_BACKOFF_MS = 30_000;

/**
 * Returns 0 for "no pacing": hint absent, or zero or negative. The LATEST response decides, so
 * callers assign this over their previous stamp rather than taking a max. An unhinted response
 * ends the pacing a hinted one started, which is exactly what the server just said.
 */
export function pacedUntil(now: number, hintMs: number | undefined): number {
  if (hintMs === undefined || hintMs <= 0) return 0;
  return now + Math.min(hintMs, MAX_WRITE_BACKOFF_MS);
}

/**
 * A stamp in the past is a no-op. The wait re-clamps to MAX_WRITE_BACKOFF_MS because awaitPace
 * cannot tell a legitimately clamped stamp from one a backward clock step stretched.
 */
export async function awaitPace(notBefore: number, now: () => number): Promise<void> {
  const remaining = Math.min(notBefore - now(), MAX_WRITE_BACKOFF_MS);
  if (remaining <= 0) return;
  await new Promise((r) => { setTimeout(r, remaining); });
}
